import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { fmtMoney } from './fmtMoney';

/**
 * Renders per-line denial codes as compact monospace chips.
 * The full code description is NOT shown inline (it is long and noisy for the
 * selection table); instead it is revealed on hover/tap via a Tooltip.
 * Exact-duplicate codes (same type + code + description) are collapsed.
 * Shows the line-level shortfall (billed - paid) once as a muted figure.
 */
export default function AdjustmentList({ denialCodes = [], billedAmount, paidAmount }) {
  const shortfall = parseFloat(billedAmount || 0) - parseFloat(paidAmount || 0);
  const hasShortfall = shortfall > 0;

  if (!denialCodes || denialCodes.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
        —
      </Typography>
    );
  }

  // Collapse exact-duplicate codes while preserving first-seen order.
  const seen = new Set();
  const uniqueCodes = [];
  denialCodes.forEach((dc) => {
    const label = `${dc.code_type || ''}-${dc.code || ''}`.replace(/^-|-$/, '');
    const key = `${label}::${dc.description || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    uniqueCodes.push({ ...dc, label });
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {uniqueCodes.map((dc, i) => {
          const chip = (
            <Chip
              label={dc.label}
              size="small"
              variant="outlined"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.68rem',
                height: 20,
                borderRadius: '3px',
                borderColor: 'text.disabled',
                color: 'text.secondary',
                flexShrink: 0,
                cursor: dc.description ? 'help' : 'default',
              }}
            />
          );

          if (!dc.description) {
            return <Box component="span" key={i}>{chip}</Box>;
          }

          return (
            <Tooltip
              key={i}
              title={
                <Typography variant="caption" sx={{ fontSize: '0.72rem', lineHeight: 1.4 }}>
                  {dc.description}
                </Typography>
              }
              arrow
              placement="top"
              enterTouchDelay={0}
              leaveTouchDelay={4000}
            >
              <Box component="span">{chip}</Box>
            </Tooltip>
          );
        })}
      </Box>
      {hasShortfall && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontSize: '0.7rem', mt: 0.25, fontStyle: 'italic' }}
        >
          Shortfall: {fmtMoney(shortfall)}
        </Typography>
      )}
    </Box>
  );
}
